import { defineStore } from 'pinia'

import type { IFeedbackStoreState } from '@/modules/feedback/types/IFeedbackStoreState'
import type { IAlertType } from '@/modules/feedback/types/IAlertType'

export const useFeedbackStore = defineStore('feedbackStore', {
  state: (): IFeedbackStoreState => ({
    isGlobalLoading: false,
    isGlobalChatLoading: false,
    loaderDebounceDelay: 700,
    isAwaitTranscription: false,
    transcriptionTimeout: null,
    transcriptionRequestId: null,
    toastTrigger: 1,
    toast: {
      severity: 'warning',
      summary: '',
      detail: '',
      life: 3000,
    },
    deletePopup: false,
    createOrUpdateAdverseEvent: false,
    loadingOnGetItem: false,
    loadingOnConfirmModal: false,
    serverErrors: {},
    isDialogVisible: false,
    globalAdverseEventId: null as number | null,
    globalTicketId: null as number | null,
    globalImprovementId: null as number | null,
    globalCitizenInquiryId: null as string | null,
    globalResponsibilityEntryId: null as number | null,
    globalAuditNonconformityReportId: null as number | null,
    globalAuditNonconformityAuditId: null as number | null,
    globalFormTab: null as number | null,
  }),
  getters: {},
  actions: {
    /**
     * Отображает всплывающее уведомление (toast) в интерфейсе
     *
     * Параметры:
     * @param type - Тип уведомления (success, info, warn, error)
     * @param title - Заголовок уведомления
     * @param message - Основной текст сообщения
     * @param time - Время отображения в миллисекундах.
     *   По умолчанию: error — 7000мс, остальные типы — 3000мс.
     *
     * Особенности:
     * - Управляет состоянием через увеличение счетчика toastTrigger
     * - Поддерживает стандартные типы уведомлений PrimeVue
     * - Позволяет гибко настраивать время отображения
     *
     * Используется для информирования пользователя о результатах операций
     */
    showToast({
      type,
      message,
      time,
    }: {
      type: IAlertType
      message: string
      time?: number
    }) {
      this.toast.severity = type
      this.toast.detail = message
      this.toast.life = time ?? (type === 'error' ? 7000 : 3000)
      this.toastTrigger += 1
    },

    /**
     * Основное действие:
     * - Устанавливает флаг createOrUpdatePopup в true
     * - Открывает модальное окно формы создания
     *
     * Особенности:
     * - Не принимает параметров
     * - Не выполняет сброса данных формы
     */
    showAdverseEventForm() {
      // Открываем форму создания/редактирования
      this.createOrUpdateAdverseEvent = true
    },

    /**
     * Переход на родительский маршрут текущей страницы.
     *
     * Приоритет стратегий:
     * 1. `router.back()` — если в истории браузера есть предыдущая запись
     *    (`window.history.state.back`). Позволяет сохранить позицию скролла
     *    родительского списка через `scrollBehavior.savedPosition`.
     * 2. `router.push({ name: parentName })` — fallback при отсутствии истории
     *    (например, открытие карточки по прямой ссылке). Родителем считается
     *    предпоследний элемент в `matched`-цепочке текущего маршрута.
     *
     * Применение:
     * - Закрытие модальных окон и форм редактирования.
     * - Возврат к предыдущему уровню навигации.
     * - Обработка отмены действий пользователем.
     */
    async goToParentRoute() {
      /* Статический импорт даёт цикл router → страницы → feedbackStore → router и ломает HMR */
      const { default: router } = await import('@/router')
      const matched = router.currentRoute.value.matched
      const parentName = matched[matched.length - 2]?.name as string | undefined
      if (parentName) {
        return router.replace({ name: parentName })
      }
    },
  },
})
